/**
 * The URL decisions behind the full-site embed (`embed.js`), which frames a
 * customer's help center on a page of their own site and deep-links into it
 * from that page's URL fragment: `/help#/content/reset-password` opens that
 * article.
 *
 * The fragment belongs to whoever wrote the link, not to the customer, so it
 * may choose a PATH on the help center and nothing else. It used to be
 * concatenated onto the origin, which let `#.evil.com/login` frame
 * `https://acme.helpcenter.io.evil.com/login`, and `#@evil.com/login` frame
 * evil.com with the help center's host read as userinfo — attacker content
 * inside the customer's own page. Every URL the embed frames is now resolved
 * against the help center's origin and kept only if it is still on it.
 *
 * Dependency-free ESM, so `tests/js/embed/frame-url.test.mjs` runs it under
 * plain `node --test`.
 */

const getEnvDomain = function (env) {
    switch (env) {
        case 'staging':
            return 'helpcenter-io-staging.com';
        case 'dev':
            return 'helpcenter-io.test';
        case 'production':
            return 'helpcenter.io';
        default:
            return 'helpcenter.io';
    }
};

/**
 * The help center's origin, from the snippet's `_hcEmbedOpts` — `customDomain`,
 * else `subdomain` under the `env` domain — with the original snippet's global
 * `subdomain` variable as the fallback. Null when neither names a help center.
 *
 * Normalised through URL so it compares equal to `MessageEvent.origin` and
 * `URL.origin` (lower case, punycode).
 *
 * @param {{env?: string, customDomain?: string, subdomain?: string}} opts
 * @param {unknown} globalSubdomain
 * @returns {string|null}
 */
export function embedOrigin(opts, globalSubdomain) {
    const subdomain = typeof opts.subdomain === 'string' ? opts.subdomain : globalSubdomain;
    let host = null;

    if (typeof opts.customDomain === 'string' && opts.customDomain) {
        host = opts.customDomain;
    } else if (typeof subdomain === 'string' && subdomain) {
        host = subdomain + '.' + getEnvDomain(opts.env || 'production');
    }

    if (!host) {
        return null;
    }

    try {
        return new URL('https://' + host).origin;
    } catch (e) {
        return null;
    }
}

/**
 * A path on the help center (`/content/x`, with any query and fragment) as an
 * absolute URL on `origin`, or null for anything that is not one.
 *
 * @param {string} origin
 * @param {unknown} path
 * @returns {string|null}
 */
function pathFrameUrl(origin, path) {
    // One leading slash, not followed by another or by a backslash (either
    // turns what follows into a host). The origin check is the actual gate:
    // the URL parser also drops tabs and newlines, so `/\t/evil.com` passes
    // this test and still resolves to evil.com.
    if (typeof path !== 'string' || !/^\/(?![/\\])/.test(path)) {
        return null;
    }

    let url;
    try {
        url = new URL(path, origin);
    } catch (e) {
        return null;
    }

    return url.origin === origin ? url.href : null;
}

/**
 * The page to frame first, from the host page's `location.hash`: the path it
 * names on the help center, or the help center's home page for anything else.
 *
 * @param {string} origin
 * @param {string} hash
 * @returns {string}
 */
export function initialFrameUrl(origin, hash) {
    return pathFrameUrl(origin, hash.charAt(0) === '#' ? hash.slice(1) : hash) || origin + '/';
}

/**
 * Where a link clicked inside the framed help center navigates the frame: the
 * help center's own absolute URL or a path on it. Null (stay put) otherwise.
 *
 * `jsembed.js` has already cancelled the click, so a null here is a dead link.
 * It posts any href that starts with its own origin, which is not the same
 * test as "is on it": `origin:443/x` and `origin` (the home page) are on it,
 * `origin.evil.com` and `origin@evil.com` are not. So an absolute href is
 * parsed and kept only if the URL parser puts it on the help center's origin.
 *
 * @param {string} origin
 * @param {unknown} href
 * @returns {string|null}
 */
export function linkFrameUrl(origin, href) {
    if (typeof href !== 'string') {
        return null;
    }

    if (href.charAt(0) === '/') {
        return pathFrameUrl(origin, href);
    }

    let url;
    try {
        url = new URL(href);
    } catch (e) {
        return null;
    }

    return url.origin === origin ? url.href : null;
}
